import { useState, useEffect, useRef } from 'react';
import { Storage } from '../storage';
import { timeAgo } from '../utils';
import Icon from './Icon';

// ─── NOTIFICATION BELL ────────────────────────────────────────────────────────
//
//  Self-contained notification bell with dropdown panel.
//  Polls unread-count every 30 s; fetches + marks-read when opened.
//
//  Props:
//    userId          {string}    current user's id (restart polling on change)
//    onViewTimeline  {function}  (timelineId) => void — navigate to a timeline
//

const NotificationBell = ({ userId, onViewTimeline, onViewProfile }) => {
  const [unreadCount,    setUnreadCount]    = useState(0);
  const [open,           setOpen]           = useState(false);
  const [notifications,  setNotifications]  = useState([]);
  const [loading,        setLoading]        = useState(false);

  const wrapperRef = useRef(null);
  const pollRef    = useRef(null);

  // ── Polling ───────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!userId) return;

    const fetchCount = () => {
      Storage.getUnreadCount()
        .then(c => setUnreadCount(c))
        .catch(() => {});
    };

    fetchCount();
    pollRef.current = setInterval(fetchCount, 30_000);
    return () => clearInterval(pollRef.current);
  }, [userId]);

  // ── Outside-click to close ────────────────────────────────────────────────

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (!wrapperRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // ── Open handler — fetch then mark read ───────────────────────────────────

  const handleOpen = async () => {
    setOpen(true);
    setLoading(true);
    try {
      const { notifications: notifs } = await Storage.getNotifications();
      setNotifications(notifs ?? []);
      setLoading(false);
      setUnreadCount(0);
      Storage.markNotificationsRead().catch(() => {});
    } catch {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    if (open) { setOpen(false); } else { handleOpen(); }
  };

  const handleItemClick = (notif) => {
    setOpen(false);
    if (notif.type === 'follow') {
      onViewProfile?.(notif.actorId);
    } else {
      onViewTimeline(notif.timelineId);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>

      {/* Bell button */}
      <button
        onClick={handleToggle}
        className="btn-ghost btn-sm"
        aria-label={`Notifications${unreadCount ? ` — ${unreadCount} unread` : ''}`}
        aria-expanded={open}
        style={{
          position: 'relative',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 34, padding: 0,
        }}
      >
        <Icon name="bell" size={15} />
        {unreadCount > 0 && (
          <span
            aria-hidden="true"
            style={{
              position: 'absolute', top: -3, right: -3,
              background: 'var(--danger)', color: '#fff',
              borderRadius: 10, fontSize: 9, fontWeight: 700,
              minWidth: 16, height: 16, padding: '0 4px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              lineHeight: 1, letterSpacing: 0,
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div
          role="region"
          aria-label="Notifications"
          style={{
            position: 'absolute', top: 'calc(100% + 10px)', right: 0,
            width: 340, maxHeight: 480, overflowY: 'auto',
            background: 'var(--surface)', border: '1px solid var(--border2)',
            borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow)',
            zIndex: 300,
          }}
        >
          {/* Header */}
          <div style={{
            padding: '13px 16px',
            borderBottom: '1px solid var(--border)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            position: 'sticky', top: 0,
            background: 'var(--surface)',
          }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
              Notifications
            </span>
            <button
              onClick={() => setOpen(false)}
              aria-label="Close notifications"
              style={{
                background: 'none', border: 'none', color: 'var(--text3)',
                cursor: 'pointer', display: 'flex', padding: 4,
              }}
            >
              <Icon name="close" size={13} />
            </button>
          </div>

          {/* Body */}
          {loading ? (
            <div style={{ padding: '32px 0', textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
              Loading…
            </div>
          ) : notifications.length === 0 ? (
            <div style={{ padding: '44px 16px', textAlign: 'center' }}>
              <div style={{ fontSize: 36, marginBottom: 12 }} aria-hidden="true">🔔</div>
              <p style={{ fontSize: 13, color: 'var(--text2)', fontWeight: 500, marginBottom: 6 }}>
                No notifications yet
              </p>
              <p style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.5 }}>
                You'll see alerts here when someone likes or comments on your timelines.
              </p>
            </div>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {notifications.map((notif, i) => (
                <NotifItem
                  key={notif.id}
                  notif={notif}
                  isLast={i === notifications.length - 1}
                  onClick={() => handleItemClick(notif)}
                />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

// ── Single notification row ────────────────────────────────────────────────────

const NotifItem = ({ notif, isLast, onClick }) => {
  const initials = (notif.actorName || '?')
    .trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase();

  return (
    <li>
      <button
        onClick={onClick}
        style={{
          width: '100%', textAlign: 'left',
          display: 'flex', alignItems: 'flex-start', gap: 11,
          padding: '11px 16px',
          background: notif.isRead ? 'transparent' : 'rgba(201,169,110,0.07)',
          border: 'none',
          borderBottom: isLast ? 'none' : '1px solid var(--border)',
          cursor: 'pointer',
          transition: 'background 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg3)'; }}
        onMouseLeave={e => { e.currentTarget.style.background = notif.isRead ? 'transparent' : 'rgba(201,169,110,0.07)'; }}
      >
        {/* Actor avatar */}
        <div style={{ flexShrink: 0, width: 36, height: 36 }}>
          {notif.actorAvatar ? (
            <img
              src={notif.actorAvatar}
              alt=""
              style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }}
            />
          ) : (
            <div aria-hidden="true" style={{
              width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
              background: 'linear-gradient(135deg, var(--accent), var(--accent3))',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12, fontWeight: 700, color: 'var(--on-accent)',
            }}>
              {initials}
            </div>
          )}
        </div>

        {/* Text */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.45, marginBottom: 3 }}>
            {notif.message}
          </p>
          <p style={{
            fontSize: 11, color: 'var(--text3)',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            marginBottom: 2,
          }}>
            {notif.timelineTitle}
          </p>
          <p style={{ fontSize: 11, color: 'var(--text3)' }}>
            {timeAgo(notif.createdAt)}
          </p>
        </div>

        {/* Unread dot */}
        {!notif.isRead && (
          <div
            aria-hidden="true"
            style={{
              width: 7, height: 7, borderRadius: '50%',
              background: 'var(--accent)', flexShrink: 0, marginTop: 5,
            }}
          />
        )}
      </button>
    </li>
  );
};

export default NotificationBell;
